import { useState, useEffect } from 'react';
import { Button, Input, Avatar, Tooltip, Typography, Space, message, Modal, ConfigProvider } from 'antd';
import { UserOutlined, MessageOutlined, DeleteOutlined, CloseCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { API_ENDPOINTS } from './config';

dayjs.extend(relativeTime);
const { Text } = Typography;

// ======================
// Comment Component
// ======================
const Comment = ({ author, avatar, content, datetime, actions }) => (
    <div className="ant-comment" style={{ display: 'flex', marginBottom: 4 }}>
        <div style={{ marginRight: 10 }}>{avatar}</div>
        <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 2 }}>
                <span style={{ fontWeight: 500 }}>{author}</span>
                <span style={{ marginLeft: 8, fontSize: 10, color: '#999' }}>{datetime}</span>
            </div>
            <div style={{ marginBottom: 4 }}>{content}</div>
            <div>{actions}</div>
        </div>
    </div>
);

// ======================
// Helper: Build nested comments
// ======================
const nestComments = (flatList) => {
    const map = {};
    const roots = [];

    flatList.forEach(c => {
        map[c.id] = { ...c, replies: [] };
    });

    flatList.forEach(c => {
        if (c.parent_id) {
            if (map[c.parent_id]) {
                map[c.parent_id].replies.push(map[c.id]);
            }
        } else {
            roots.push(map[c.id]);
        }
    });

    return roots;
};

const CommentsSection = ({ blogId }) => {
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [replyingTo, setReplyingTo] = useState(null);
    const [commentText, setCommentText] = useState('');
    const [replyText, setReplyText] = useState('');
    const [userName, setUserName] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [tempName, setTempName] = useState('');
    const [showComments, setShowComments] = useState(true);

    useEffect(() => {
        const savedName = localStorage.getItem('commentUserName') || '';
        if (savedName) {
            setUserName(savedName);
        } else {
            setIsModalVisible(true);
        }
    }, []);

    const saveUserName = () => {
        if (!tempName.trim()) {
            message.warning('Please enter your name');
            return;
        }
        setUserName(tempName);
        localStorage.setItem('commentUserName', tempName);
        setIsModalVisible(false);
    };

    const fetchComments = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API_ENDPOINTS.LIST_COMMENTS}&blog_id=${blogId}`);
            const safeData = (data.data || []).map(c => ({
                ...c,
                user_name: c.user_name || 'Anonymous',
                comment_text: c.comment_text || '',
                created_at: c.created_at || null
            }));
            setComments(nestComments(safeData)); // build tree
        } catch {
            message.error('Failed to load comments');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchComments();
    }, [blogId]);

    const handleCommentSubmit = async () => {
        if (!commentText.trim()) return message.warning('Please enter a comment');
        if (!userName.trim()) return message.warning('Please enter your name');

        setSubmitting(true);
        try {
            await axios.post(API_ENDPOINTS.ADD_COMMENT, {
                blog_id: blogId,
                user_name: userName,
                comment_text: commentText,
            });
            setCommentText('');
            fetchComments();
            message.success('Comment added successfully');
        } catch {
            message.error('Failed to post comment');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReplySubmit = async (parentId) => {
        if (!replyText.trim()) return message.warning('Please enter a reply');

        setSubmitting(true);
        try {
            await axios.post(API_ENDPOINTS.ADD_REPLY, {
                blog_id: blogId,
                parent_id: parentId,
                user_name: userName,
                comment_text: replyText,
            });
            setReplyText('');
            setReplyingTo(null);
            fetchComments();
            message.success('Reply added successfully');
        } catch {
            message.error('Failed to post reply');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (commentId) => {
        try {
            await axios.post(API_ENDPOINTS.DELETE_COMMENT, { id: commentId });
            fetchComments();
            message.success('Comment deleted successfully');
        } catch {
            message.error('Failed to delete comment');
        }
    };

    const renderActions = (comment) => {
        const actions = [];
        if (userName === 'Admin Shiny') {
            actions.push(
                <Tooltip key="reply" title="Reply">
                    <span onClick={() => setReplyingTo(comment.id)}>
                        <MessageOutlined /> Reply
                    </span>
                </Tooltip>
            );
        }
        if (userName === 'Admin Shiny') {
            actions.push(
                <Tooltip key="delete" title="Delete">
                    <span onClick={() => handleDelete(comment.id)}>
                        <DeleteOutlined /> Delete
                    </span>
                </Tooltip>
            );
        }
        return actions;
    };

    const renderCommentTree = (comment, level = 0) => (
        <div key={comment.id} style={{ marginLeft: level * 40, marginTop: 8 }}>
            <Comment
                author={<Text strong>{comment.user_name === 'Admin Shiny' ? 'Shiny' : comment.user_name}</Text>}
                avatar={<Avatar icon={<UserOutlined />} />}
                content={<Text>{comment.comment_text}</Text>}
                datetime={
                    <Tooltip title={comment.created_at ? dayjs(comment.created_at).format('YYYY-MM-DD HH:mm:ss') : 'Unknown'}>
                        <span>{comment.created_at ? dayjs(comment.created_at).fromNow() : 'Unknown'}</span>
                    </Tooltip>
                }
                actions={renderActions(comment)}
            />
            {replyingTo === comment.id && (
                <div style={{ marginLeft: 48, marginTop: 8 }}>
                    <Input.TextArea
                        rows={2}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder={`Reply to ${comment.user_name}`}
                    />
                    <Space style={{ marginTop: 8 }}>
                        <Button
                            size="small"
                            onClick={() => handleReplySubmit(comment.id)}
                            loading={submitting}
                            disabled={!replyText.trim()}
                        >
                            Post Reply
                        </Button>
                        <Button size="small" onClick={() => setReplyingTo(null)}>Cancel</Button>
                    </Space>
                </div>
            )}
            {comment.replies?.map(reply => renderCommentTree(reply, level + 1))}
        </div>
    );

    return (
        <ConfigProvider>
            <div className="comments-section">
                {showComments && (
                    <Button
                        type="text"
                        icon={<CloseCircleOutlined />}
                        style={{ float: 'right', color: 'white', backgroundColor: 'tomato', marginTop: '20px' }}
                        onClick={() => setShowComments(false)}
                    >
                    </Button>
                )}

                {/* Modal for username */}
                <Modal
                    title="Enter Your Name"
                    open={isModalVisible}
                    onOk={saveUserName}
                    footer={null}
                    closable={false}
                >
                    <Input
                        placeholder="Your name"
                        value={tempName}
                        onChange={(e) => setTempName(e.target.value)}
                        onPressEnter={saveUserName}
                        autoFocus
                    />
                    <Button type="primary" onClick={saveUserName} style={{ marginTop: 10 }}>
                        Save Name
                    </Button>
                </Modal>

                {showComments && (
                    <>
                        {/* Add comment */}
                        <Space direction="vertical" style={{ width: '100%' }}>
                            {userName && <Text type="secondary">Commenting as: {userName}</Text>}
                            <Input.TextArea
                                rows={2}
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                placeholder="Write your comment..."
                            />
                            <Button
                                type="primary"
                                onClick={handleCommentSubmit}
                                loading={submitting}
                                disabled={!commentText.trim()}
                            >
                                Post Comment
                            </Button>
                        </Space>

                        {/* Render comments */}
                        {loading ? (
                            <p>Loading comments...</p>
                        ) : (
                            comments.map(c => renderCommentTree(c))
                        )}
                    </>
                )}
            </div>
        </ConfigProvider>
    );
};

export default CommentsSection;
